<script setup>
import { inject, ref, defineEmits, watch } from 'vue';
import { debounce, get } from 'lodash';
import { ElSelect, ElOption } from 'element-plus';
import 'element-plus/es/components/select/style/css';
import 'element-plus/es/components/option/style/css';
import 'element-plus/es/components/form-item/style/css'
import { useCompanyStore } from '@dutypay/store-modules';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { locale } = useI18n({ useScope: 'global' });
const composables = inject('composables');
const { isRTL } = composables.useJurisdictionSwitch()
const companyStore = useCompanyStore();
const emit = defineEmits(['switch'])
const input = ref({});
const loading = ref(false);
const debounceRate = 500; // milliseconds

async function switchCompany(company) {
  await companyStore.switchCompany(company);
  emit('switch');
}

watch(locale, async () => {
  await companyStore.initCompanyStore();
})

const searchCompanies = async (query) => {
  if (query === '')
    return;
  loading.value = true;
  await companyStore.fetchCompanies({ query });
  loading.value = false;
}
</script>
<script>
export default {
  name: "DpSelectCompanySwitch"
}
</script>

<template>
  <el-select v-model="input"
             v-loading="loading"
             :placeholder="t('company.placeholder')"
             :no-match-text="t('noMatch.label')"
             :no-data-text="t('noData.label')"
             v-on:change="switchCompany($event)"
             :popper-class="isRTL ? 'right-to-left' : ''"
             filterable
             :remote="companyStore.companiesCountTotal > 5"
             :remote-method="debounce(searchCompanies, debounceRate)">
    <el-option
        v-for="(item, index) in get(companyStore, 'companies', [])"
        :key="index"
        :label="item.name.legal"
        :value="item">
    </el-option>
  </el-select>
</template>

<i18n>
{
  "ar-AE": {
    "noData": {
      "label": "لا توجد بيانات لعرض"
    },
    "company": {
      "placeholder": "الرجاء تحديد شركة"
    },
    "noMatch": {
      "label": "لا توجد بيانات لعرض!"
    }
  },
  "ar-EG": {
    "noData": {
      "label": "لا توجد بيانات لعرض"
    },
    "company": {
      "placeholder": "الرجاء تحديد شركة"
    },
    "noMatch": {
      "label": "لا توجد بيانات لعرض!"
    }
  },
  "ar-SA": {
    "noData": {
      "label": "لا توجد بيانات لعرض"
    },
    "company": {
      "placeholder": "الرجاء تحديد شركة"
    },
    "noMatch": {
      "label": "لا توجد بيانات لعرض!"
    }
  },
  "de-DE": {
    "noData": {
      "label": "Keine Daten zum Anzeigen"
    },
    "company": {
      "placeholder": "Unternehmen auswählen"
    },
    "noMatch": {
      "label": "Keine Daten zum Anzeigen!"
    }
  },
  "de-AT": {
    "noData": {
      "label": "Keine Daten zum Anzeigen"
    },
    "company": {
      "placeholder": "Unternehmen auswählen"
    },
    "noMatch": {
      "label": "Keine Daten zum Anzeigen!"
    }
  },
  "de-CH": {
    "noData": {
      "label": "Keine Daten zum Anzeigen"
    },
    "company": {
      "placeholder": "Unternehmen auswählen"
    },
    "noMatch": {
      "label": "Keine Daten zum Anzeigen!"
    }
  },
  "en-US": {
    "noData": {
      "label": "No data to display"
    },
    "company": {
      "placeholder": "Select a company"
    },
    "noMatch": {
      "label": "No data to display!"
    }
  },
  "en-GB": {
    "noData": {
      "label": "No data to display"
    },
    "company": {
      "placeholder": "Select a company"
    },
    "noMatch": {
      "label": "No data to display!"
    }
  },
  "it-IT": {
    "noData": {
      "label": "Nessun dato da visualizzare!"
    },
    "company": {
      "placeholder": "Seleziona un'azienda"
    },
    "noMatch": {
      "label": "Nessun dato da visualizzare!"
    }
  },
  "fr-FR": {
    "noData": {
      "label": "Aucune donnée à afficher!"
    },
    "company": {
      "placeholder": "Veuillez sélectionner une entreprise"
    },
    "noMatch": {
      "label": "Aucune donnée à afficher!"
    }
  },
  "es-ES": {
    "noData": {
      "label": "¡No hay información para mostrar!"
    },
    "company": {
      "placeholder": "Seleccione una empresa"
    },
    "noMatch": {
      "label": "¡No hay información para mostrar!"
    }
  },
  "pl-PL": {
    "noData": {
      "label": "Brak danych do wyświetlenia!"
    },
    "company": {
      "placeholder": "Wybierz firmę"
    },
    "noMatch": {
      "label": "Brak danych do wyświetlenia!"
    }
  },
  "nl-NL": {
    "noData": {
      "label": "Geen gegevens om weer te geven!"
    },
    "company": {
      "placeholder": "Selecteer een bedrijf"
    },
    "noMatch": {
      "label": "Geen gegevens om weer te geven!"
    }
  },
  "bg-BG": {
    "noData": {
      "label": "Няма данни за показване!"
    },
    "company": {
      "placeholder": "Моля, изберете фирма"
    },
    "noMatch": {
      "label": "Няма данни за показване!"
    }
  },
  "bs-BA": {
    "noData": {
      "label": "Nema podataka za prikaz!"
    },
    "company": {
      "placeholder": "Molimo odaberite kompaniju"
    },
    "noMatch": {
      "label": "Nema podataka za prikaz!"
    }
  },
  "da-DK": {
    "noData": {
      "label": "Ingen data at vise!"
    },
    "company": {
      "placeholder": "Vælg et firma"
    },
    "noMatch": {
      "label": "Ingen data at vise!"
    }
  },
  "et-EE": {
    "noData": {
      "label": "Pole andmeid, mida kuvada!"
    },
    "company": {
      "placeholder": "Valige ettevõte"
    },
    "noMatch": {
      "label": "Pole andmeid, mida kuvada!"
    }
  },
  "fi-FI": {
    "noData": {
      "label": "Ei näytettäviä tietoja!"
    },
    "company": {
      "placeholder": "Valitse yritys"
    },
    "noMatch": {
      "label": "Ei näytettäviä tietoja!"
    }
  },
  "el-GR": {
    "noData": {
      "label": "Δεν υπάρχουν δεδομένα προς εμφάνιση!"
    },
    "company": {
      "placeholder": "Επιλέξτε εταιρεία"
    },
    "noMatch": {
      "label": "Δεν υπάρχουν δεδομένα προς εμφάνιση!"
    }
  },
  "he-IL": {
    "noData": {
      "label": "אין נתונים להצגה!"
    },
    "company": {
      "placeholder": "אנא בחר חברה"
    },
    "noMatch": {
      "label": "אין נתונים להצגה!"
    }
  },
  "ga-IE": {
    "noData": {
      "label": "Gan aon sonraí le taispeáint!"
    },
    "company": {
      "placeholder": "Roghnaigh cuideachta le do thoil"
    },
    "noMatch": {
      "label": "Gan aon sonraí le taispeáint!"
    }
  },
  "ja-JP": {
    "noData": {
      "label": "表示するデータがありません！"
    },
    "company": {
      "placeholder": "会社を選択してください"
    },
    "noMatch": {
      "label": "表示するデータがありません！"
    }
  },
  "hr-HR": {
    "noData": {
      "label": "Nema podataka za prikaz!"
    },
    "company": {
      "placeholder": "Odaberite tvrtku"
    },
    "noMatch": {
      "label": "Nema podataka za prikaz!"
    }
  },
  "lv-LV": {
    "noData": {
      "label": "Nav datu, ko parādīt!"
    },
    "company": {
      "placeholder": "Lūdzu, izvēlieties uzņēmumu"
    },
    "noMatch": {
      "label": "Nav datu, ko parādīt!"
    }
  },
  "lt-LT": {
    "noData": {
      "label": "Nėra rodomų duomenų!"
    },
    "company": {
      "placeholder": "Pasirinkite įmonę"
    },
    "noMatch": {
      "label": "Nėra rodomų duomenų!"
    }
  },
  "pt-PT": {
    "noData": {
      "label": "Sem dados para exibir!"
    },
    "company": {
      "placeholder": "Selecione uma empresa"
    },
    "noMatch": {
      "label": "Sem dados para exibir!"
    }
  },
  "ro-RO": {
    "noData": {
      "label": "Nu sunt date de afișat!"
    },
    "company": {
      "placeholder": "Vă rugăm să selectați o companie"
    },
    "noMatch": {
      "label": "Nu sunt date de afișat!"
    }
  },
  "ru-RU": {
    "noData": {
      "label": "Нет данных для отображения!"
    },
    "company": {
      "placeholder": "Пожалуйста, выберите компанию"
    },
    "noMatch": {
      "label": "Нет данных для отображения!"
    }
  },
  "sv-SE": {
    "noData": {
      "label": "Ingen data att visa!"
    },
    "company": {
      "placeholder": "Välj ett företag"
    },
    "noMatch": {
      "label": "Ingen data att visa!"
    }
  },
  "sk-SK": {
    "noData": {
      "label": "Žiadne údaje na zobrazenie!"
    },
    "company": {
      "placeholder": "Vyberte spoločnosť"
    },
    "noMatch": {
      "label": "Žiadne údaje na zobrazenie!"
    }
  },
  "cs-CZ": {
    "noData": {
      "label": "Žádná data k zobrazení!"
    },
    "company": {
      "placeholder": "Vyberte společnost"
    },
    "noMatch": {
      "label": "Žádná data k zobrazení!"
    }
  },
  "tr-TR": {
    "noData": {
      "label": "Gösterilecek bilgi yok!"
    },
    "company": {
      "placeholder": "Lütfen bir şirket seçin"
    },
    "noMatch": {
      "label": "Gösterilecek bilgi yok!"
    }
  },
  "hu-HU": {
    "noData": {
      "label": "Nincs megjeleníthető adat!"
    },
    "company": {
      "placeholder": "Válasszon egy vállalatot"
    },
    "noMatch": {
      "label": "Nincs megjeleníthető adat!"
    }
  },
  "fr-BE": {
    "noData": {
      "label": "Aucune donnée à afficher!"
    },
    "company": {
      "placeholder": "Veuillez sélectionner une entreprise"
    },
    "noMatch": {
      "label": "Aucune donnée à afficher!"
    }
  },
  "fr-LU": {
    "noData": {
      "label": "Aucune donnée à afficher!"
    },
    "company": {
      "placeholder": "Veuillez sélectionner une entreprise"
    },
    "noMatch": {
      "label": "Aucune donnée à afficher!"
    }
  },
  "mt-MT": {
    "noData": {
      "label": "L-ebda dejta li trid turi"
    },
    "company": {
      "placeholder": "Jekk jogħġbok agħżel kumpanija"
    },
    "noMatch": {
      "label": "L-ebda dejta li trid turi"
    }
  },
  "sl-SI": {
    "noData": {
      "label": "Ni podatkov za prikaz!"
    },
    "company": {
      "placeholder": "Izberite podjetje"
    },
    "noMatch": {
      "label": "Ni podatkov za prikaz!"
    }
  },
  "el-CY": {
    "noData": {
      "label": "Δεν υπάρχουν δεδομένα προς εμφάνιση!"
    },
    "company": {
      "placeholder": "Επιλέξτε εταιρεία"
    },
    "noMatch": {
      "label": "Δεν υπάρχουν δεδομένα προς εμφάνιση!"
    }
  },
  "tr-CY": {
    "noData": {
      "label": "Gösterilecek bilgi yok!"
    },
    "company": {
      "placeholder": "Lütfen bir şirket seçin"
    },
    "noMatch": {
      "label": "Gösterilecek bilgi yok!"
    }
  },
  "uk-UA": {
    "noData": {
      "label": "Немає даних для відображення"
    },
    "company": {
      "placeholder": "Виберіть компанію"
    },
    "noMatch": {
      "label": "Немає даних для відображення"
    }
  },
  "zh-CN": {
    "noData": {
      "label": "没有数据显示！"
    },
    "company": {
      "placeholder": "请选择公司"
    },
    "noMatch": {
      "label": "没有数据显示！"
    }
  }
}
</i18n>
